import { useCallback } from 'react';
import { omit } from 'lodash';

import { useBoolean } from '@blockworks/platform/client';
import { useTrack } from '@blockworks/platform/services';
import { DownloadIcon, ExportFileIcon, ExportTableIcon, Menu, Popover } from '@blockworks/ui';

import { useVisualizationOptionsContext } from '../../context';
import { AnalyticsEvent } from '../../utils/events-tracking';
import { FeatureControlButton } from '../feature-control-button';
import { SubscribeToAccess } from '../subscription-gates';

import { ExportExecutionFormat, ExportExecutionResultsProps } from './export-execution-results.model';
import { getExportUrl, makeExportClickHandler } from './export-execution-results.utils';

const ExportExecutionResults = ({
    visualization,
    isGated: isGatedProp,
    trackVisualizationFallback,
}: ExportExecutionResultsProps) => {
    const popover = useBoolean(false);
    const { features } = useVisualizationOptionsContext();
    const track = useTrack();
    const onClick = useCallback(
        (format: ExportExecutionFormat) => {
            const eventKey = format === 'csv' ? AnalyticsEvent.ExportResultsCsv : AnalyticsEvent.ExportResultsJson;
            track?.(`${visualization?.type ?? 'Visualization'} > ${eventKey}`, {
                visualization: omit(visualization ?? trackVisualizationFallback, ['execution', 'config']),
            });
        },
        [track, visualization, trackVisualizationFallback],
    );
    const isGated = isGatedProp ?? (!!features?.dangerouslyGateDataExport || !visualization);

    return (
        <Popover
            open={popover.value}
            onOpenChange={popover.setValue}
            trigger={<FeatureControlButton intent={'primary-soft'} borderRadius={'md'} icon={DownloadIcon} />}
            contentProps={{ align: isGated ? 'center' : 'end', side: 'bottom' }}
        >
            {isGated ? (
                <SubscribeToAccess
                    trackEvent={{ event: AnalyticsEvent.SubscribeToExportClicked, data: { visualization } }}
                >
                    To export datasets, sign up or log in to Research.
                </SubscribeToAccess>
            ) : (
                <Menu className="p-1">
                    <Menu.List>
                        <Menu.Item
                            indent={0}
                            size="xs"
                            icon={ExportFileIcon}
                            href={getExportUrl('json', visualization)}
                            onClick={makeExportClickHandler({ format: 'json', onClick, visualization })}
                        >
                            Export JSON
                        </Menu.Item>
                        <Menu.Item
                            indent={0}
                            size="xs"
                            icon={ExportTableIcon}
                            href={getExportUrl('csv', visualization)}
                            onClick={makeExportClickHandler({ format: 'csv', onClick, visualization })}
                        >
                            Export CSV
                        </Menu.Item>
                    </Menu.List>
                </Menu>
            )}
        </Popover>
    );
};

export type { ExportExecutionResultsProps };
export { ExportExecutionResults };
